<template>
  <v-skeleton-loader
    v-if="!dataset"
    class="mr-2 mt-6"
    type="list-item-three-line"
  ></v-skeleton-loader>
  <v-form v-else ref="dataset_form">
    <v-row>
      <v-col cols="12">
        <v-text-field 
          type="text"
          v-model="dataset.datasetName"
          :label="$vuetify.lang.t('$vuetify.gpt_dataset.dataset_name')"
          :rules="[
            required($vuetify.lang.t('$vuetify.gpt_dataset.dataset_name')),
          ]"
          :error-messages="fieldErrorFor('name')"
          outlined
          clearable
          >
          <template v-slot:append>
            <v-tooltip right max-width="350">
              <template v-slot:activator="{ on }">
                <span v-on="on" class="info-icon">i</span>
              </template>
              <span>
                {{ $vuetify.lang.t('$vuetify.gpt_dataset.dataset_name_hint') }}
              </span>
            </v-tooltip>
          </template>
        </v-text-field>
      </v-col>
      
      <v-col cols="12">
        <v-text-field
          type="text"
          v-model="dataset.description"
          :label="$vuetify.lang.t('$vuetify.gpt_dataset.description_label_txt')"
          :error-messages="fieldErrorFor('description')"
          outlined
          clearable
        >
        <template v-slot:append>
            <v-tooltip right max-width="350">
              <template v-slot:activator="{ on }">
                <span v-on="on" class="info-icon">i</span>
              </template>
              <span>
                {{ $vuetify.lang.t('$vuetify.gpt_dataset.dataset_description_hint') }}
              </span>
            </v-tooltip>
          </template>
      </v-text-field>
      </v-col>
    
      <v-col cols="12" md="12">
        <v-combobox
          v-model="dataset.tags"
          :items="tagChips"
          :rules="tagsRule"
          :error-messages="fieldErrorFor('tags')"
          :hint="$vuetify.lang.t('$vuetify.gpt_dataset.dataset_tags_hint')"
          :label="$vuetify.lang.t('$vuetify.gpt_profile.dataset_tags')"
          variant="solo"
          chips
          clearable
          outlined
          @change="onDatasetTagsChange"
          multiple
          :class="{ 'no-dropdown-icon': !showtags }"
          persistent-hint
        >
          <template v-slot:no-data>
            <div v-if="showtags && formattedTagsItems.length > 0" class="px-4 py-2">
              <div class="d-flex mb-3 pb-2 header-row">
                <div class="dataset-name-column text-subtitle-1 font-weight-bold">{{$vuetify.lang.t('$vuetify.gpt_dataset.datasets')}}</div>
                <div class="tags-column text-subtitle-1 font-weight-bold">{{$vuetify.lang.t('$vuetify.access_tab.tags')}}</div>
              </div>
              <div 
                v-for="datasetItem in formattedTagsItems" 
                :key="datasetItem.dataset" 
                class="d-flex mb-3 pb-2 data-row"
              >
                <div class="dataset-name-column">
                  <v-tooltip bottom v-if="isDatasetNameTruncated(datasetItem.dataset)">
                    <template v-slot:activator="{ on, attrs }">
                      <div 
                        class="text-subtitle-2 font-weight-bold dataset-name-text"
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ truncateDatasetName(datasetItem.dataset) }}:
                      </div>
                    </template>
                    <span class="tooltip-text">{{ (datasetItem.dataset || '').toUpperCase() }}</span>
                  </v-tooltip>
                  
                  <div 
                    v-else
                    class="text-subtitle-2 font-weight-bold dataset-name-text"
                  >
                    {{ truncateDatasetName(datasetItem.dataset) }}:
                  </div>
                </div>
                
                <div class="tags-column">
                  <div class="tag-badges">
                    <v-chip
                      v-for="tag in datasetItem.tags"
                      :key="`${datasetItem.dataset}-${tag}`"
                      small
                      class="mr-1 mb-1 clickable"
                      :color="tagSelected(tag) ? 'primary' : 'default'"
                      @click.stop="toggleTag(tag)"
                      clickable
                    >
                      {{ tag }}
                    </v-chip>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <template v-slot:selection="{ attrs, item, selected }">
            <v-chip @click:close="removeDatasetTags(item)" v-bind="attrs" :model-value="selected" closable close>
              <span>{{ item }}</span
              >&nbsp;
            </v-chip>
          </template>
        </v-combobox>
      </v-col>
      <v-col v-if="!isCloudCreate" cols="12">
        <v-autocomplete
          v-model="dataset.indexColumnName"
          :items="isUpdateMode ? [dataset.indexColumnName] : (df ? df.columns : [])"
          :label="$vuetify.lang.t('$vuetify.gpt_dataset.index_column_name')"
          :error-messages="fieldErrorFor('idx_column_name')"
          :disabled="isUpdateMode || !df"
          outlined
          clearable
          append-icon="mdi-menu-down"
        >
          <template v-slot:append>
            <div class="append-icons">
              <v-icon>mdi-menu-down</v-icon>
              <v-tooltip right max-width="350">
                <template v-slot:activator="{ on }">
                  <span v-on="on" class="info-icon">i</span>
                </template>
                <span>
                  {{ $vuetify.lang.t('$vuetify.gpt_dataset.index_column_name_hint') }}
                </span>
              </v-tooltip>
            </div>
          </template>
        </v-autocomplete>
      </v-col>
      <!-- Cloud-create index column (typed manually — no single file to auto-detect columns from) -->
      <v-col v-if="isCloudCreate" cols="12">
        <v-combobox
          v-model="dataset.indexColumnName"
          :items="cloudColumnSuggestions"
          :label="$vuetify.lang.t('$vuetify.gpt_dataset.index_column_name')"
          :error-messages="fieldErrorFor('idx_column_name')"
          :hint="$vuetify.lang.t('$vuetify.gpt_dataset.index_column_name_hint')"
          persistent-hint
          clearable
          outlined
        />
      </v-col>
      <v-col v-if="!isCloudCreate" cols="12" md="12">
        <v-autocomplete
          v-model="selectedDatetimeColumns"
          :items="isUpdateMode ? selectedDatetimeColumns : potentialDatetimeColumns"
          :label="$vuetify.lang.t('$vuetify.gpt_dataset.datetime_columns')"
          :disabled="isUpdateMode || !df"
          :rules="datetimeColumnsRule"
          :error-messages="datetimeColumnsErrorMessages"
          @update:model-value="updatePrimaryOptions"
          multiple
          small-chips
          deletable-chips 
          outlined 
          append-icon="mdi-menu-down"
          >
          <template v-slot:append>
            <div class="append-icons">
              <v-icon>mdi-menu-down</v-icon>
              <v-tooltip right max-width="350">
                <template v-slot:activator="{ on }">
                  <span v-on="on" class="info-icon">i</span>
                </template>
                <span>
                  {{ $vuetify.lang.t('$vuetify.gpt_dataset.datetime_columns_hint') }}
                </span>
              </v-tooltip>
            </div>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col v-if="!isCloudCreate" cols="12" md="12">
        <v-autocomplete
          v-model="selectedPrimaryDatetimeColumn"
          :items="selectedDatetimeColumns"
          @change="onPrimaryDatetimeChange"
          :label="$vuetify.lang.t('$vuetify.gpt_dataset.primary_datetime_column')"
          outlined
          :disabled="!isUpdateMode && (!df || selectedDatetimeColumns.length === 0)"
          :rules="primaryDatetimeRule"
          :error-messages="primaryDatetimeErrorMessages"
          >
          <template v-slot:append>
            <div class="append-icons">
              <v-icon>mdi-menu-down</v-icon>
              <v-tooltip right max-width="350">
                <template v-slot:activator="{ on }">
                  <span v-on="on" class="info-icon">i</span>
                </template>
                <span>
                  {{ $vuetify.lang.t('$vuetify.gpt_dataset.primary_datetime_column_hint') }}
                </span>
              </v-tooltip>
            </div>
          </template>
        </v-autocomplete>
      </v-col>
      <!-- Cloud-create datetime columns (typed manually — no file to auto-detect from) -->
      <v-col v-if="isCloudCreate" cols="12" md="12">
        <v-combobox
          v-model="selectedDatetimeColumns"
          :items="potentialDatetimeColumns"
          :label="$vuetify.lang.t('$vuetify.gpt_dataset.datetime_columns')"
          :rules="datetimeColumnsRule"
          :error-messages="datetimeError"
          :hint="$vuetify.lang.t('$vuetify.gpt_dataset.datetime_columns_hint')"
          persistent-hint
          multiple
          small-chips
          deletable-chips
          outlined
          @change="updatePrimaryOptions"
        />
      </v-col>
      <v-col v-if="isCloudCreate" cols="12" md="12">
        <v-combobox
          v-model="selectedPrimaryDatetimeColumn"
          :items="selectedDatetimeColumns"
          :label="
            $vuetify.lang.t('$vuetify.gpt_dataset.primary_datetime_column')
          "
          :rules="primaryDatetimeRule"
          :error-messages="primaryDatetimeError"
          :hint="
            $vuetify.lang.t('$vuetify.gpt_dataset.primary_datetime_column_hint')
          "
          persistent-hint
          outlined
          @change="onPrimaryDatetimeChange"
        />
      </v-col>

      <!-- Cloud storage source (shared with the regular dataset form and the
           documents upload modal) -->
      <UploadSourceSelector
        :source="dataset.uploadSource || 'local'"
        :cloud-sources="dataset.cloudSources || []"
        :selected-provider="dataset.cloudProvider || ''"
        @change="onUploadSourceChange"
        @open-picker="openCloudPickerFor"
        @remove-item="handleRemoveCloudItem"
        @clear-provider="handleClearCloudProvider"
      />

      <!-- Cloud sync settings -->
      <v-col v-if="dataset.uploadSource === 'cloud'" cols="12" md="6">
        <v-select
          :value="dataset.cloudUpdateFrequency"
          :label="
            $vuetify.lang.t(
              '$vuetify.gpt_dataset.datasetType.update_frequency.label',
            )
          "
          outlined
          :rules="[
            required(
              $vuetify.lang.t(
                '$vuetify.gpt_dataset.datasetType.update_frequency.label',
              ),
            ),
          ]"
          :items="cloudFrequencyOptions"
          @input="handleCloudFrequencyChange"
        />
      </v-col>

      <v-col
        v-if="
          dataset.uploadSource === 'cloud' &&
          dataset.cloudUpdateFrequency === 'custom'
        "
        cols="12"
        md="6"
      >
        <v-text-field
          :value="dataset.cloudSyncHours"
          type="number"
          outlined
          :label="
            $vuetify.lang.t(
              '$vuetify.gpt_dataset.cloud_storage.sync_hours_label',
            )
          "
          :hint="
            $vuetify.lang.t(
              '$vuetify.gpt_dataset.cloud_storage.sync_hours_hint',
            )
          "
          persistent-hint
          placeholder="e.g. 6"
          :rules="cloudSyncHoursRules"
          @input="handleCloudSyncHoursChange"
        />
      </v-col>

      <v-col v-if="dataset.uploadSource === 'cloud' && hasFolderSelected" cols="12" md="6">
        <v-select
          :value="dataset.cloudExtensions"
          :items="cloudFolderExtensionOptions"
          :label="
            $vuetify.lang.t(
              '$vuetify.gpt_dataset.cloud_storage.extensions_label',
            )
          "
          :hint="
            $vuetify.lang.t(
              '$vuetify.gpt_dataset.cloud_storage.extensions_hint_ts',
            )
          "
          persistent-hint
          chips
          small-chips
          deletable-chips
          multiple
          outlined
          @input="handleCloudExtensionsChange"
        >
          <template #selection="{ item }">
            <v-chip
              small
              close
              @click:close="
                handleCloudExtensionsChange(
                  (dataset.cloudExtensions || []).filter((e) => e !== item),
                )
              "
            >
              .{{ item }}
            </v-chip>
          </template>
        </v-select>
      </v-col>

      <v-col cols="12" md="12">
        <v-card outlined class="pa-4">
          <div class="text-subtitle-2 mb-4">
            {{ $vuetify.lang.t("$vuetify.gpt_dataset.timezone_policy") }}
          </div>

          <v-row dense>
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="masterTimezone"
                :items="timezoneItems"
                :label="$vuetify.lang.t('$vuetify.gpt_dataset.master_timezone_label')"
                :placeholder="$vuetify.lang.t('$vuetify.gpt_dataset.policy_not_set')"
                :hint="$vuetify.lang.t('$vuetify.gpt_dataset.master_timezone_hint')"
                :rules="masterTimezoneRule"
                :error-messages="masterTimezoneErrorMessages"
                @change="onMasterTimezoneChange"
                dense
                outlined
                clearable
                persistent-hint
                persistent-placeholder
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" md="4">
              <v-select
                v-model="naivePolicyChoice"
                :items="naivePolicyItems"
                :label="$vuetify.lang.t('$vuetify.gpt_dataset.naive_policy_label')"
                :hint="$vuetify.lang.t('$vuetify.gpt_dataset.naive_policy_hint')"
                :rules="policyRule"
                :error-messages="policyErrorMessages"
                @change="onPolicyChange"
                dense
                outlined
                persistent-hint
              ></v-select>
            </v-col>

            <v-col cols="12" md="4">
              <v-select
                v-model="awarePolicyChoice"
                :items="awarePolicyItems"
                :label="$vuetify.lang.t('$vuetify.gpt_dataset.aware_policy_label')"
                :hint="$vuetify.lang.t('$vuetify.gpt_dataset.aware_policy_hint')"
                :rules="policyRule"
                :error-messages="policyErrorMessages"
                @change="onPolicyChange"
                dense
                outlined
                persistent-hint
              ></v-select>
            </v-col>
          </v-row>

          <template v-if="selectedDatetimeColumns.length > 0">
            <v-divider class="my-4"></v-divider>
            <div class="d-flex align-center mb-2">
              <span class="text-caption grey--text text--darken-1">
                {{ $vuetify.lang.t("$vuetify.gpt_dataset.column_timezones") }}
              </span>
              <v-tooltip right max-width="350">
                <template v-slot:activator="{ on }">
                  <span v-on="on" class="info-icon">i</span>
                </template>
                <span>
                  {{ $vuetify.lang.t("$vuetify.gpt_dataset.column_timezones_hint") }}
                </span>
              </v-tooltip>
            </div>
            <v-row v-for="(row, index) in datetimeFieldRows" :key="row.name" dense align="center">
              <v-col cols="12" md="5" class="py-1">
                <span class="text-body-2 column-name">{{ row.name }}</span>
                <v-chip v-if="row.isPrimary" x-small class="ml-2">
                  {{ $vuetify.lang.t("$vuetify.gpt_dataset.primary_short") }}
                </v-chip>
              </v-col>
              <v-col cols="12" md="7" class="py-1">
                <v-autocomplete
                  :value="row.timezone"
                  :items="timezoneItems"
                  :placeholder="inheritPlaceholder"
                  :rules="columnTimezoneRules"
                  :error-messages="columnErrorMessagesFor(row, index)"
                  @change="onColumnTimezoneChange(row.name, $event)"
                  dense
                  outlined
                  clearable
                  persistent-placeholder
                  hide-details="auto"
                ></v-autocomplete>
              </v-col>
            </v-row>
          </template>
        </v-card>
      </v-col>

      <v-col cols="12" md="12" v-if="timezoneFormErrors.length > 0">
        <v-alert v-for="(msg, index) in timezoneFormErrors" :key="index" dense text type="error" class="mb-2">
          {{ msg }}
        </v-alert>
      </v-col>

      <v-col cols="12" md="12" v-if="legacyTimezoneWarning">
        <v-alert dense text type="warning" class="mb-0">
          {{ legacyTimezoneWarning }}
        </v-alert>
      </v-col>

      <v-col cols="12" md="12" v-if="ingestionNoticeLines.length > 0">
        <v-alert dense text :type="ingestionNoticeType" class="mb-0">
          <div v-for="(line, index) in ingestionNoticeLines" :key="index">{{ line }}</div>
        </v-alert>
      </v-col>
      <v-col cols="12" md="12" v-if="!isProcessing && dataset.uploadSource !== 'cloud'">
        <v-file-input
          outlined
          v-model="file"
          accept=".csv,.json,.xlsx,.parquet"
          @change="handleFileUpload"
          :label="$vuetify.lang.t('$vuetify.gpt_dataset.dataset_file')"
          :rules="fileInputRules"
          :hint="$vuetify.lang.t('$vuetify.gpt_dataset.encoding_hint')"
          persistent-hint
          clearable
          prepend-icon="mdi-paperclip"
          :error-messages="fileErrorMessages"
          class="dataset-file-input"
        >
          <template v-slot:append>
            <div class="append-icons">
              <v-tooltip right max-width="350">
                <template v-slot:activator="{ on }">
                  <span v-on="on" class="info-icon">i</span>
                </template>
                <span>
                  {{ $vuetify.lang.t("$vuetify.gpt_dataset.file_support_type_ts") }}
                </span>
              </v-tooltip>
            </div>
          </template>
        </v-file-input>
      </v-col>

      <v-col cols="12" md="12" v-if="isProcessing">
        <v-skeleton-loader class="mr-2 mt-6" type="list-item-three-line"></v-skeleton-loader>
        <div class="text-center mt-2">
          <span class="text-body-2">{{ $vuetify.lang.t('$vuetify.gpt_dataset.processing_file') }}</span>
        </div>
      </v-col>

      <v-col cols="12" md="12" v-if="df && !isProcessing && !fileErrorMessages.length">
        <v-card outlined class="pa-3">
          <div class="d-flex justify-space-between align-center mb-3">
            <div>
              <div class="text-h6">{{ file.name }}</div>
              <div class="text-body-2 grey--text">
                {{ fileStats.totalRows }} {{ $vuetify.lang.t('$vuetify.gpt_dataset.rows') }} × {{ fileStats.totalColumns }} {{ $vuetify.lang.t('$vuetify.gpt_dataset.column') }}
              </div>
            </div>
            <div>
              <v-btn small outlined color="primary" class="mr-2" @click="showPreviewModal = true">
                <v-icon small class="mr-1">mdi-eye</v-icon>
                {{ $vuetify.lang.t('$vuetify.gpt_dataset.preview') }}
              </v-btn>
              <v-btn small outlined color="info" @click="showStatsModal = true">
                <v-icon small class="mr-1">mdi-chart-bar</v-icon>
                {{ $vuetify.lang.t('$vuetify.gpt_dataset.stats') }}
              </v-btn>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog v-model="showPreviewModal" max-width="900px">
      <v-card>
        <v-card-title class="text-h5">
          {{ $vuetify.lang.t('$vuetify.gpt_dataset.file_preview') }}
          <v-spacer></v-spacer>
        </v-card-title>
        <v-card-text>
          <div class="mb-3">
            <strong>{{ $vuetify.lang.t('$vuetify.gpt_dataset.file') }}:</strong> {{ file ? file.name : '' }} |
            <strong>{{ $vuetify.lang.t('$vuetify.gpt_dataset.rows') }}:</strong> {{ fileStats.totalRows }} |
            <strong>{{ $vuetify.lang.t('$vuetify.gpt_dataset.column') }}:</strong> {{ fileStats.totalColumns }}
          </div>
          <div class="preview-table-container">
            <v-simple-table fixed-header height="384px">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th v-for="header in filePreview?.headers || []" :key="header" class="text-left">
                      {{ header }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in filePreview?.rows || []" :key="index">
                    <td v-for="(value, colIndex) in row" :key="colIndex">
                      {{ formatPreviewValue(value) }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </v-card-text>
        <v-card-actions class="pa-4 modal-footer">
          <v-spacer></v-spacer>
          <v-btn text @click="showPreviewModal = false" class="mr-2 red--text">
            {{ $vuetify.lang.t('$vuetify.common.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showStatsModal" max-width="1000px">
      <v-card style="max-height: 60vh; display: flex; flex-direction: column;">
        <v-card-title class="text-h5" style="flex-shrink: 0;">
          {{ $vuetify.lang.t('$vuetify.gpt_dataset.column_statistics') }}
        <v-spacer></v-spacer>
      </v-card-title>
      <v-card-text style="display: flex; flex-direction: column; flex-grow: 1; overflow-y: hidden;">
        <div class="stats-table-container">
          <v-simple-table fixed-header height="384px">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">{{ $vuetify.lang.t('$vuetify.gpt_dataset.column') }}</th>
                  <th class="text-left">{{ $vuetify.lang.t('$vuetify.gpt_dataset.type') }}</th>
                  <th class="text-left">{{ $vuetify.lang.t('$vuetify.gpt_dataset.null_count') }}</th>
                  <th class="text-left">{{ $vuetify.lang.t('$vuetify.gpt_dataset.unique_values') }}</th>
                  <th class="text-left">{{ $vuetify.lang.t('$vuetify.gpt_dataset.sample_values') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="stat in columnStats" :key="stat.column">
                  <td><strong>{{ stat.column }}</strong></td>
                  <td>
                    <v-chip small :color="getTypeColor(stat.inferredType)">
                      {{ stat.inferredType }}
                    </v-chip>
                  </td>
                  <td>{{ stat.nullCount }} ({{ stat.nullPercentage }}%)</td>
                  <td>{{ stat.uniqueCount }}</td>
                  <td class="sample-values">{{ stat.sampleValues.join(', ') }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>
      </v-card-text>
      <v-card-actions style="flex-shrink: 0;">
        <v-spacer></v-spacer>
        <v-btn text @click="showStatsModal = false" class="mr-2 red--text">
          {{ $vuetify.lang.t('$vuetify.common.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </v-form>
</template>

<script>

import { mapState, mapActions } from "vuex";
import { UPDATE_GPT_DATASET_STATE, GET_GPT_DATASETS, DOWNLOAD_CLOUD_FILE, GET_CLOUD_STORAGE_FILES } from "@/store/_actiontypes";
import {
  processFile,
  generateFilePreview,
  generateColumnStats,
  formatPreviewValue,
  getTypeColor
} from "@/helpers/helper_ts";
import {
  AWARE_POLICY_CONVERT,
  AWARE_POLICY_REPLACE,
  buildDatetimeFieldsTz,
  getTimezonePickerItems,
  guessTimezone,
  datasetFieldLabelKey,
  inspectDatetimeFieldsTz,
  isValidTimezone,
  NAIVE_POLICY_CONVERT,
  NAIVE_POLICY_REPLACE,
  POLICY_AS_IS,
  policyNoticeLines,
  readDatetimeFields,
  readTimezonePolicies,
} from "@/helpers/timezone";
import { datasetMixin, removeCloudItem, removeCloudProvider } from "../../../mixins/datasetMixin";
import UploadSourceSelector from "./UploadSourceSelector.vue";

export default {
  mixins: [datasetMixin],
  name: "UploadDatasetTs",
  components: {
    UploadSourceSelector,
  },
  props: {
    propName: {
      type: String,
      default: ""
    },
    showtags: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    ...mapState({
      dataset: state => state.project.selectedGptDataset,
      gptDatasets: state => state.project.gptDatasets.datasets,
      gptSetupState: state => state.project.gptSetupState,      
      fileInputRules() {
        let rules = [];
        // Only require file in create mode or when setup is not completed
        if (!this.isUpdateMode) {
          if (!this.gptSetupState?.dataset?.completed) {
            rules.push(
              value => !!value || this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_file") +
              this.$vuetify.lang.t("$vuetify.validation.required_error_msg_txt"),
            );
          }
          if(!this.dataset?.datasetFile){
            rules.push(
              value => !!value || this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_file") +
              this.$vuetify.lang.t("$vuetify.validation.required_error_msg_txt"),
            );
          }
        }
        // Only validate file size if a file is actually selected
        rules.push(
          value => !value || value.size <= this.MAX_FILE_SIZE ||
          `${this.$vuetify.lang.t('$vuetify.gpt_dataset.file_size_exceeded_error')} ${this.MAX_FILE_SIZE / 1024 / 1024}MB`
        );
        // Only show file error if it exists
        if (this.fileError) {
          rules.push(() => this.fileError);
        }
        
        return rules;
      },
      datetimeColumnsRule() {
        // `rules` is what `form.validate()` consults on submit, so the column integrity checks gate here; `datetimeColumnsErrorMessages` is what shows them live.
        const rules = [];
        if (!this.isUpdateMode) {
          rules.push(
            v => !!v && v.length > 0 || this.$vuetify.lang.t('$vuetify.gpt_dataset.datetime_fields_required'),
            () => !this.timezoneReport.hasEmptyName || this.$vuetify.lang.t('$vuetify.gpt_dataset.datetime_column_empty_name'),
            () =>
              this.timezoneReport.duplicateNames.length === 0 ||
              this.$vuetify.lang.t('$vuetify.gpt_dataset.datetime_column_duplicate'),
            () =>
              this.timezoneReport.caseCollisions.length === 0 ||
              this.$vuetify.lang.t('$vuetify.gpt_dataset.datetime_column_case_collision'),
          );
        }
        return rules;
      },
      primaryDatetimeRule() {
        const rules = [
          () => !this.timezoneReport.primaryNotInFields || this.$vuetify.lang.t('$vuetify.gpt_dataset.primary_not_in_datetime_fields'),
        ];
        if (this.isUpdateMode || this.dataset?.datasetFile || this.isCloud) {
          rules.unshift(
            () => !!this.selectedPrimaryDatetimeColumn || this.$vuetify.lang.t('$vuetify.gpt_dataset.primary_datetime_field_required')
          );
        }
        return rules;
      },
      tagsRule() {
        return [v => !!v && v.length > 0 || this.$vuetify.lang.t('$vuetify.gpt_dataset.dataset_tags_required')];
      },
      fileErrorMessages() {
        const messages = [];
        if (this.fileError) {
          messages.push(this.fileError);
        }
        return messages;
      },
      formattedTagsItems() {
        const items = [];

        Object.entries(this.tagsMap).forEach(([datasetName, tags]) => {
          if (!Array.isArray(tags) || tags.length === 0) return;
          
          const filtered = tags.filter(t => String(t).toLowerCase() !== 'dictionary');
          
          if (filtered.length === 0) return;
      
          const sortedTags = [...filtered].sort((a, b) => {
            const aSelected = this.dataset.tags?.includes(a);
            const bSelected = this.dataset.tags?.includes(b);
            return (aSelected === bSelected) ? 0 : aSelected ? -1 : 1;
          });

          items.push({
            dataset: datasetName,
            tags: sortedTags,
          });
        });

        return items;
      }
    }),
    isUpdateMode() {
      return !!this.gptSetupState?.dataset?.dataset_id || !!this.$route.query.datasetId
    },
    isCloud() {
      return this.dataset?.uploadSource === "cloud";
    },
    flatCloudItems() {
      if (!this.isCloud) return [];
      return (this.dataset?.cloudSources || []).flatMap(source =>
        (source.items || []).map(item => ({
          ...item,
          provider: source.provider,
          account_id: item.account_id || source.account_id,
        }))
      );
    },
    // A single plain file can be downloaded and parsed client-side; folders or multi-selections have no single file to preview.
    previewableCloudItem() {
      const files = this.flatCloudItems.filter(item => !item.is_folder && !item._restored);
      return files.length === 1 ? files[0] : null;
    },
    // No single file to fully preview, but one representative file still beats offering the index/datetime column fields zero suggestions.
    sampleCloudItem() {
      if (this.previewableCloudItem) return null;
      return this.flatCloudItems.find(item => !item.is_folder && !item._restored) || null;
    },
    // Nothing selected directly is a file — fall back to the first selected folder, whose contents we'll list to find a sample.
    sampleCloudFolder() {
      if (this.previewableCloudItem || this.sampleCloudItem) return null;
      return this.flatCloudItems.find(item => item.is_folder) || null;
    },
    // Cloud create can't parse the file client-side to detect datetime columns,
    // so the user types them manually via comboboxes instead of the autocompletes.
    isCloudCreate() {
      return this.isCloud && !this.isUpdateMode;
    },
    cloudFrequencyOptions() {
      const label = key =>
        this.$vuetify.lang.t(
          `$vuetify.gpt_dataset.datasetType.update_frequency.${key}`,
        );

      return ["once", "daily", "weekly", "monthly", "yearly", "custom"].map(
        value => ({ text: label(value), value }),
      );
    },
    cloudSyncHoursRules() {
      const t = key =>
        this.$vuetify.lang.t(`$vuetify.gpt_dataset.cloud_storage.${key}`);
      return [
        this.required(t("sync_hours_label")),
        v => this.isBlank(v) || Number(v) >= 1 || t("sync_hours_min_error"),
        v => this.isBlank(v) || Number(v) <= 23 || t("sync_hours_max_error"),
        v =>
          this.isBlank(v) ||
          Number.isInteger(Number(v)) ||
          t("sync_hours_integer_error"),
      ];
    },
    timezoneItems() {
      return getTimezonePickerItems();
    },
    inheritPlaceholder() {
      const label = this.$vuetify.lang.t('$vuetify.gpt_dataset.inherit_master_timezone');
      return this.masterTimezone ? `${label} (${this.masterTimezone})` : label;
    },
    datetimeFieldRows() {
      return this.selectedDatetimeColumns.map(name => ({
        name,
        timezone: this.columnTimezones[name] || null,
        isPrimary: name === this.selectedPrimaryDatetimeColumn,
      }));
    },
    datetimeFieldsTz() {
      return buildDatetimeFieldsTz(this.selectedDatetimeColumns, this.columnTimezones);
    },
    naivePolicy() {
      return this.naivePolicyChoice === POLICY_AS_IS ? null : this.naivePolicyChoice;
    },
    awarePolicy() {
      return this.awarePolicyChoice === POLICY_AS_IS ? null : this.awarePolicyChoice;
    },
    notSetItem() {
      return { value: POLICY_AS_IS, text: this.$vuetify.lang.t("$vuetify.gpt_dataset.policy_keep_as_is") };
    },
    replaceLabel() {
      return this.$vuetify.lang.t("$vuetify.gpt_dataset.policy_replace");
    },
    convertLabel() {
      return this.$vuetify.lang.t("$vuetify.gpt_dataset.policy_convert");
    },
    naivePolicyItems() {
      return [
        { value: NAIVE_POLICY_REPLACE, text: this.replaceLabel },
        { value: NAIVE_POLICY_CONVERT, text: this.convertLabel },
        this.notSetItem,
      ];
    },
    awarePolicyItems() {
      return [
        { value: AWARE_POLICY_CONVERT, text: this.convertLabel },
        { value: AWARE_POLICY_REPLACE, text: this.replaceLabel },
        this.notSetItem,
      ];
    },
    timezoneReport() {
      return inspectDatetimeFieldsTz({
        fields: this.datetimeFieldsTz,
        masterTimezone: this.masterTimezone,
        primaryDatetimeField: this.selectedPrimaryDatetimeColumn,
        naivePolicy: this.naivePolicy,
        awarePolicy: this.awarePolicy,
      });
    },
    masterTimezoneRule() {
      const requiredMessage = !this.masterTimezoneMissing || this.$vuetify.lang.t("$vuetify.gpt_dataset.master_timezone_required");
      return [
        // Only required once a policy is set; neither-set is valid.
        () => requiredMessage,
        v => !v || isValidTimezone(v) || this.$vuetify.lang.t("$vuetify.gpt_dataset.invalid_timezone"),
      ];
    },
    policyRule() {
      return [
        // A timezone no policy consumes would never be applied.
        () =>
          !this.timezoneReport.timezoneWithoutPolicy || this.$vuetify.lang.t("$vuetify.gpt_dataset.policy_required"),
      ];
    },
    masterTimezoneMissing() {
      return (
        this.timezoneReport.policyWithoutTimezone.length > 0 || this.timezoneReport.policyWithoutAnyTimezone
      );
    },
    masterTimezoneErrorMessages() {
      const messages = [];
      if (this.masterTimezoneError) messages.push(this.masterTimezoneError);
      if (this.masterTimezoneMissing) {
        messages.push(this.$vuetify.lang.t("$vuetify.gpt_dataset.master_timezone_required"));
      }
      return messages;
    },
    policyErrorMessages() {
      return this.timezoneReport.timezoneWithoutPolicy
        ? [this.$vuetify.lang.t("$vuetify.gpt_dataset.policy_required")]
        : [];
    },
    datetimeColumnsErrorMessages() {
      const messages = [];
      if (this.datetimeError) messages.push(this.datetimeError);
      messages.push(...this.fieldErrorFor("datetime_fields_tz"));
      if (this.timezoneReport.hasEmptyName) {
        messages.push(this.$vuetify.lang.t("$vuetify.gpt_dataset.datetime_column_empty_name"));
      }
      const duplicates = this.timezoneReport.duplicateNames;
      if (duplicates.length > 0) {
        messages.push(`${this.$vuetify.lang.t("$vuetify.gpt_dataset.datetime_column_duplicate")} ${duplicates.join(', ')}`);
      }
      const caseCollisions = this.timezoneReport.caseCollisions;
      if (caseCollisions.length > 0) {
        messages.push(`${this.$vuetify.lang.t("$vuetify.gpt_dataset.datetime_column_case_collision")} ${caseCollisions.join(', ')}`);
      }
      return messages;
    },
    primaryDatetimeErrorMessages() {
      const messages = [];
      if (this.primaryDatetimeError) messages.push(this.primaryDatetimeError);
      messages.push(...this.fieldErrorFor("primary_datetime_field"));
      if (this.timezoneReport.primaryNotInFields) {
        messages.push(this.$vuetify.lang.t("$vuetify.gpt_dataset.primary_not_in_datetime_fields"));
      }
      return messages;
    },
    // One shared array so every row binds the same identity — a per-row call
    // would build a new array each render and retrigger Vuetify's rules watcher.
    columnTimezoneRules() {
      return [
        v => !v || isValidTimezone(v) || this.$vuetify.lang.t("$vuetify.gpt_dataset.invalid_timezone"),
      ];
    },
    ingestionNoticeType() {
      return this.naivePolicy ? "info" : "warning";
    },
    // Spelled out before the upload because it cannot be undone afterwards
    // without re-uploading the file.
    ingestionNoticeLines() {
      if (this.selectedDatetimeColumns.length === 0) return [];
      const t = key => this.$vuetify.lang.t(`$vuetify.gpt_dataset.${key}`);
      const mappings = this.datetimeFieldRows
        .map(row => {
          const zone = row.timezone || this.masterTimezone;
          return zone ? `${row.name} → ${zone}` : null;
        })
        .filter(Boolean);
      return policyNoticeLines({ t, naivePolicy: this.naivePolicy, awarePolicy: this.awarePolicy, mappings });
    },
    legacyTimezoneWarning() {
      if (!this.isUpdateMode) return '';
      const storedFields = readDatetimeFields(this.dataset);
      if (storedFields.length === 0) return '';
      const hasZone =
        !!this.dataset?.master_timezone || storedFields.some(field => !!field.timezone);
      return hasZone ? '' : this.$vuetify.lang.t('$vuetify.gpt_dataset.legacy_timezone_warning');
    },
  },
  data() {
    return {
      tagChips: [],
      file: null,
      df: null,
      potentialDatetimeColumns: [],
      selectedDatetimeColumns: [],
      selectedPrimaryDatetimeColumn: null,
      primaryDatetimeOptions: [],
      isProcessing: false,
      filePreview: null,
      fileStats: {
        totalRows: 0,
        totalColumns: 0
      },
      columnStats: [],
      showPreviewModal: false,
      showStatsModal: false,
      MAX_FILE_SIZE: 100 * 1024 * 1024,
      MAX_COLUMNS: 1000,
      MIN_ROWS: 1,
      SAMPLE_SIZE: 100,
      // Fields with an input bound to `fieldErrorFor`; anything else falls back
      // to a form-level message prefixed with its label.
      MAPPED_FIELD_ERRORS: [
        "name",
        "description",
        "tags",
        "idx_column_name",
        "primary_datetime_field",
        "datetime_fields_tz",
      ],
      PREVIEW_ROWS: 5,
      datetimeError: '',
      primaryDatetimeError: '',
      fileError: '',
      existingDatetimeFields: [],
      existingPrimaryDatetime: null,
      tagsMap: {},
      masterTimezone: null,
      columnTimezones: {},
      naivePolicyChoice: POLICY_AS_IS,
      awarePolicyChoice: POLICY_AS_IS,
      masterTimezoneError: "",
      columnTimezoneErrors: {},
      timezoneFormErrors: [],
      serverMissingColumns: [],
      fieldErrors: {},
      cloudPreviewItemId: null,
      cloudColumnSuggestions: [],
      cloudSuggestionItemId: null,
    };
  },
  watch: {
    "dataset.cloudSources": {
      deep: true,
      async handler() {
        await this.maybeLoadCloudColumnSuggestions();
        this.maybeLoadCloudPreview();
      },
    },
    "dataset.datasetName"() {
      this.clearFieldError("name");
    },
    "dataset.description"() {
      this.clearFieldError("description");
    },
    "dataset.tags"() {
      this.clearFieldError("tags");
    },
    "dataset.indexColumnName"() {
      this.clearFieldError("idx_column_name");
    },
    selectedPrimaryDatetimeColumn() {
      this.clearFieldError("primary_datetime_field");
    },
    selectedDatetimeColumns() {
      this.clearFieldError("datetime_fields_tz");
    },
    dataset: {
      deep: true,
      handler() {
        const localDataset = this.dataset;
        this.UPDATE_GPT_DATASET_STATE({ dataset: localDataset });

        if (this.isUpdateMode && localDataset) {
          const storedFields = readDatetimeFields(localDataset);
          this.existingDatetimeFields = storedFields.map(field => field.name);
          this.existingPrimaryDatetime = localDataset.primary_datetime_field || null;

          if (!this.file) {
            this.selectedDatetimeColumns = [...this.existingDatetimeFields];
            this.selectedPrimaryDatetimeColumn = this.existingPrimaryDatetime || null;
          }
          this.seedTimezones(localDataset, storedFields);
        }
      },
    },
    gptDatasets: {
      immediate: true,
      handler(datasets) {
        if (!Array.isArray(datasets)) {
          return;
        }
        this.tagsMap = {};
        datasets.forEach((dataset, index) => {
          const key = dataset.name?.trim() || `dataset_${index + 1}`;
          this.tagsMap[key] = dataset.tags || [];
        });
      }
    },
    file: {
      handler(newFile) {
        if (!newFile) {
          this.UPDATE_GPT_DATASET_STATE({
            dataset: { ...this.dataset, datasetFile: null },
          });
          if (!this.isUpdateMode) {
            this.selectedDatetimeColumns = [];
            this.selectedPrimaryDatetimeColumn = null;
          } else {
            this.selectedDatetimeColumns = [...this.existingDatetimeFields];
            this.selectedPrimaryDatetimeColumn = this.existingPrimaryDatetime || null;
          }
        }
      },
    },
  },
  mounted() {
    if (this.isUpdateMode && this.dataset) {
      const storedFields = readDatetimeFields(this.dataset);
      this.existingDatetimeFields = storedFields.map(field => field.name);
      this.existingPrimaryDatetime = this.dataset.primary_datetime_field;

      this.selectedDatetimeColumns = [...this.existingDatetimeFields];
      this.selectedPrimaryDatetimeColumn = this.existingPrimaryDatetime || null;
      this.indexColumnName = this.dataset.indexColumnName || null;
      this.seedTimezones(this.dataset, storedFields);
    } else {
      // Pre-select what the API used to do unconditionally: attach the zone to
      // naive values, convert aware ones.
      this.masterTimezone = guessTimezone();
      this.naivePolicyChoice = NAIVE_POLICY_REPLACE;
      this.awarePolicyChoice = AWARE_POLICY_CONVERT;
    }
    this.fetchTags();
  },
  methods: {
    ...mapActions("project", [UPDATE_GPT_DATASET_STATE, GET_GPT_DATASETS, DOWNLOAD_CLOUD_FILE, GET_CLOUD_STORAGE_FILES]),
    formatPreviewValue,
    getTypeColor,

    onUploadSourceChange(source) {
      // Re-selecting the active source must not wipe what the user configured.
      if (source === (this.dataset.uploadSource || "local")) return;
      this.UPDATE_GPT_DATASET_STATE({
        dataset: {
          ...this.dataset,
          uploadSource: source,
          datasetFile: null,
          ...(source === "cloud" && {
            cloudUpdateFrequency: this.dataset?.cloudUpdateFrequency || "once",
            cloudSyncHours: this.dataset?.cloudSyncHours ?? null,
          }),
        },
      });
      this.file = null;
      this.resetState();
    },
    handleRemoveCloudItem({ provider, itemId }) {
      const cloudSources = removeCloudItem(
        this.dataset.cloudSources,
        provider,
        itemId,
      );
      this.UPDATE_GPT_DATASET_STATE({
        dataset: { ...this.dataset, cloudSources, cloudSourcesDirty: true },
      });
    },
    handleClearCloudProvider(provider) {
      const cloudSources = removeCloudProvider(this.dataset.cloudSources, provider);
      this.UPDATE_GPT_DATASET_STATE({
        dataset: { ...this.dataset, cloudSources, cloudSourcesDirty: true },
      });
    },
    handleCloudFrequencyChange(val) {
      this.UPDATE_GPT_DATASET_STATE({
        dataset: {
          ...this.dataset,
          cloudUpdateFrequency: val,
          ...(val !== "custom" && { cloudSyncHours: null }),
        },
      });
    },
    handleCloudSyncHoursChange(val) {
      this.UPDATE_GPT_DATASET_STATE({
        dataset: {
          ...this.dataset,
          cloudSyncHours: val === "" ? null : Number(val),
        },
      });
    },

    async fetchTags() {
      try {
        await this.GET_GPT_DATASETS({
          project_id: this.$route.query.pid,
          size: 100
        });
      } catch (error) {
        console.error("Error fetching datasets for tags:", error);
      }
    },

    tagSelected(tag) {
      return this.dataset.tags?.includes(tag) || false;
    },

    toggleTag(tag) {
      if (!this.dataset.tags) {
        this.dataset.tags = [];
      }
      
      const index = this.dataset.tags.indexOf(tag);
      if (index === -1) {
        this.dataset.tags.push(tag);
      } else {
        this.dataset.tags.splice(index, 1);
      }
    },

    truncateDatasetName(datasetName) {
      if (!datasetName) return '';
      const upperCaseName = datasetName.toUpperCase();
      const maxLength = 30;
      
      if (upperCaseName.length <= maxLength) {
        return upperCaseName;
      }
      
      return upperCaseName.substring(0, maxLength) + '...';
    },

    isDatasetNameTruncated(datasetName) {
      if (!datasetName) return false;
      return datasetName.toUpperCase().length > 30;
    },

    required(fieldName) {
      return (value) => {
        return !!value || `${fieldName}${this.$vuetify.lang.t('$vuetify.validation.required_error_msg_txt')}`;
      };
    },
    seedTimezones(localDataset, storedFields) {
      this.masterTimezone = localDataset.master_timezone ?? null;
      this.columnTimezones = storedFields.reduce((acc, field) => {
        acc[field.name] = field.timezone ?? null;
        return acc;
      }, {});

      // Datasets predating policies come back with both null, which is the
      // API's "store everything as it arrives" state.
      const { naivePolicy, awarePolicy } = readTimezonePolicies(localDataset);
      this.naivePolicyChoice = naivePolicy ?? POLICY_AS_IS;
      this.awarePolicyChoice = awarePolicy ?? POLICY_AS_IS;
    },
    onMasterTimezoneChange() {
      this.clearTimezoneErrors();
      this.syncTimezonesToStore();
    },
    onColumnTimezoneChange(name, value) {
      this.columnTimezones = { ...this.columnTimezones, [name]: value || null };
      this.clearTimezoneErrors();
      this.syncTimezonesToStore();
    },
    onPolicyChange() {
      this.clearTimezoneErrors();
      this.syncTimezonesToStore();
    },
    clearTimezoneErrors() {
      this.masterTimezoneError = "";
      this.columnTimezoneErrors = {};
      this.timezoneFormErrors = [];
      this.serverMissingColumns = [];
    },
    clearFieldError(field) {
      if (!this.fieldErrors[field]) return;
      const remaining = { ...this.fieldErrors };
      delete remaining[field];
      this.fieldErrors = remaining;
    },
    // Server-side error for one named field, so it marks that input rather than
    // appearing as an unattributed sentence at the bottom of the form.
    fieldErrorFor(field) {
      const message = this.fieldErrors[field];
      return message ? [message] : [];
    },
    // A 422 keys column errors by index, a 400 names them.
    columnErrorMessagesFor(row, index) {
      if (this.columnTimezoneErrors[index]) return [this.columnTimezoneErrors[index]];
      if (this.serverMissingColumns.includes(row.name)) {
        return [this.$vuetify.lang.t("$vuetify.gpt_dataset.column_timezone_required")];
      }
      return [];
    },
    // The update payload is assembled from the store, so timezone edits have to
    // land there.
    syncTimezonesToStore() {
      this.UPDATE_GPT_DATASET_STATE({
        dataset: {
          ...this.dataset,
          master_timezone: this.masterTimezone || null,
          naive_policy: this.naivePolicy,
          aware_policy: this.awarePolicy,
          datetime_fields_tz: this.datetimeFieldsTz,
        },
      });
    },
    // Called by the parent with a parsed error body.
    setTimezoneErrors(parsed) {
      this.masterTimezoneError = parsed?.master || '';
      this.columnTimezoneErrors = parsed?.columns || {};
      this.serverMissingColumns = parsed?.missingColumns || [];
      this.fieldErrors = parsed?.fields || {};

      // A field with no input of its own still has to say which field it is,
      // so prefix its label rather than showing a bare sentence.
      const unmapped = Object.entries(this.fieldErrors)
        .filter(([field]) => !this.MAPPED_FIELD_ERRORS.includes(field))
        .map(([field, message]) => {
          const labelKey = datasetFieldLabelKey(field);
          const label = labelKey ? this.$vuetify.lang.t(labelKey) : field;
          return `${label}: ${message}`;
        });
      this.timezoneFormErrors = [...(parsed?.form || []), ...unmapped];
    },
    updatePrimaryOptions() {
      if (!this.selectedDatetimeColumns.includes(this.selectedPrimaryDatetimeColumn)) {
        this.selectedPrimaryDatetimeColumn = null;
      }
      this.primaryDatetimeOptions = [...this.selectedDatetimeColumns];
    },
    onDatasetTagsChange(val) {
      const trimmed = val.map(tag => tag.trim()).filter(tag => tag !== "");
      this.dataset.tags = Array.from(new Set(trimmed));
    },
    removeDatasetTags(item){
      this.dataset.tags.splice(this.dataset.tags.indexOf(item), 1)
    },
    getDatasetInfo() {
      const info = {
        name: this.dataset.datasetName,
        index_column_name: this.dataset.indexColumnName,
        description: this.dataset.description,
        file: this.file,
        filename: this.file?.name || '',
        masterTimezone: this.masterTimezone || null,
        naivePolicy: this.naivePolicy,
        awarePolicy: this.awarePolicy,
        datetimeFieldsTz: this.datetimeFieldsTz,
        primaryDatetimeColumn: this.selectedPrimaryDatetimeColumn,
        tags: this.dataset.tags,
      };

      // Remove index_column_name if empty string
      if (info.index_column_name === "") {
        delete info.index_column_name;
      }

      if (this.isCloud) {
        info.cloud_update_frequency =
          this.dataset.cloudUpdateFrequency || "once";
        if (
          info.cloud_update_frequency === "custom" &&
          this.dataset.cloudSyncHours
        ) {
          info.cloud_sync_hours = Number(this.dataset.cloudSyncHours);
        }
        if (this.dataset.cloudExtensions?.length) {
          info.cloud_extensions = this.dataset.cloudExtensions;
        }
      }

      return info;
    },
    validateDatetimeFields() {
      if (this.isUpdateMode && this.existingDatetimeFields.length > 0) {
        const missingFields = this.existingDatetimeFields.filter(
          field => !this.df.columns.includes(field)
        );

        if (missingFields.length > 0) {
          throw new Error(`${this.$vuetify.lang.t('$vuetify.gpt_dataset.datetime_fields_not_available_error')} ${missingFields.join(', ')}`);
        }

        if (this.existingPrimaryDatetime && !this.df.columns.includes(this.existingPrimaryDatetime)) {
          throw new Error(`${this.$vuetify.lang.t('$vuetify.gpt_dataset.missing_primary_field')} ${this.existingPrimaryDatetime}`);
        }
      }
    },
    resetState() {
      this.df = null;

      this.filePreview = null;
      this.fileStats = { totalRows: 0, totalColumns: 0 };
      this.columnStats = [];
      this.showPreviewModal = false;
      this.showStatsModal = false;
      this.potentialDatetimeColumns = [];
      this.primaryDatetimeOptions = [];
      this.availableIndexColumns = [];
      this.isProcessing = false;
      this.datetimeError = '';
      this.primaryDatetimeError = '';
      this.fileError = '';
      this.clearTimezoneErrors();
      this.fieldErrors = {};

      if (!this.isUpdateMode) {
        this.selectedDatetimeColumns = [];
        this.selectedPrimaryDatetimeColumn = null;
      }
    },
    validateFile() {
      if (!this.file) {
        throw new Error(this.$vuetify.lang.t('$vuetify.gpt_dataset.no_file_selected'));
      }

      if (this.file.size > this.MAX_FILE_SIZE) {
        throw new Error(`${this.$vuetify.lang.t('$vuetify.gpt_dataset.file_size_exceeded_error')} ${this.MAX_FILE_SIZE / 1024 / 1024}MB`);
      }

      const allowedExtensions = ['csv', 'json', 'xlsx', 'parquet'];
      const fileExtension = this.file.name.split('.').pop().toLowerCase();

      if (!allowedExtensions.includes(fileExtension)) {
        throw new Error(this.$vuetify.lang.t('$vuetify.gpt_dataset.unsupported_file_error') + ` ${fileExtension}`);
      }
    },
    validateDataset() {
      if (!this.df || this.df.shape[0] < this.MIN_ROWS) {
        throw new Error( 
          this.$vuetify.lang.t('$vuetify.gpt_dataset.min_column_error', { count: this.MIN_ROWS }) + ` Current: ${this.df ? this.df.shape[0] : 0}`);
      }

      if (this.df.shape[1] > this.MAX_COLUMNS) {
        throw new Error(
          this.$vuetify.lang.t('$vuetify.gpt_dataset.max_column_error') + 
          ` (${this.df.shape[1]}). ` +
          this.$vuetify.lang.t('$vuetify.gpt_dataset.max_column_allowed', { count: this.MAX_COLUMNS })
        );
      }

      this.fileStats = {
        totalRows: this.df.shape[0],
        totalColumns: this.df.shape[1]
      };
    },
    onPrimaryDatetimeChange(value) {
      if (this.isCloudCreate && value && !this.selectedDatetimeColumns.includes(value)) {
        this.selectedDatetimeColumns = [...this.selectedDatetimeColumns, value];
      }
      const known = this.selectedDatetimeColumns.includes(value) ? value : null;
      this.selectedPrimaryDatetimeColumn = known;
      this.UPDATE_GPT_DATASET_STATE({
        dataset: { ...this.dataset, primary_datetime_field: known }
      });
    },
    // A folder's contents aren't known until listed, and a matching file may sit inside a subfolder rather than at the top level — so this walks the tree breadth-first,
    // one page per folder, until it finds a file or runs out of budget. Just a hint, not an authority, so a bounded scan beats fetching the whole tree.
    async findSampleFileInFolder(folder, identity) {
      const MAX_FOLDERS_TO_SCAN = 20;
      const queue = [{ id: folder.id, provider: folder.provider, account_id: folder.account_id }];
      let scanned = 0;
      while (queue.length && scanned < MAX_FOLDERS_TO_SCAN) {
        if (this.cloudSuggestionItemId !== identity) return null;
        const current = queue.shift();
        scanned += 1;
        const res = await this.GET_CLOUD_STORAGE_FILES({
          project_id: this.$route.query.pid,
          provider: current.provider,
          account_id: current.account_id,
          folder_id: current.id,
          limit: 50,
          extensions: this.cloudFileExtensions.join(","),
        });
        const entries = res.data?.detail?.files || [];
        const found = entries.find(e => !e.is_folder);
        if (found) return { ...found, provider: current.provider, account_id: current.account_id };
        entries
          .filter(e => e.is_folder)
          .forEach(e => queue.push({ id: e.id, provider: current.provider, account_id: current.account_id }));
      }
      return null;
    },
    // Multi-file/folder cloud selections get no full preview (maybeLoadCloudPreview only handles a lone file), but the index/datetime column fields — and the preview/stats card — still shouldn't be left blank.
    // Sample one representative file via the same download endpoint and run it through the same parse pipeline, minus dataset-level validation — other files/folders in the batch may differ, so this is a hint, not an authority.
    async maybeLoadCloudColumnSuggestions() {
      if (!this.isCloudCreate || this.previewableCloudItem) return;
      const directItem = this.sampleCloudItem;
      const folder = this.sampleCloudFolder;
      const identity = directItem ? directItem.id : folder ? `folder:${folder.id}` : null;
      if (!identity) {
        this.cloudColumnSuggestions = [];
        this.potentialDatetimeColumns = [];
        this.cloudSuggestionItemId = null;
        this.df = null;
        this.file = null;
        this.filePreview = null;
        this.columnStats = [];
        this.fileStats = { totalRows: 0, totalColumns: 0 };
        return;
      }
      if (this.cloudSuggestionItemId === identity) return;
      this.cloudSuggestionItemId = identity;
      this.isProcessing = true;
      try {
        const item = directItem || (await this.findSampleFileInFolder(folder, identity));
        if (this.cloudSuggestionItemId !== identity) return;
        if (!item) {
          this.cloudColumnSuggestions = [];
          this.potentialDatetimeColumns = [];
          this.df = null;
          this.file = null;
          this.filePreview = null;
          this.columnStats = [];
          this.fileStats = { totalRows: 0, totalColumns: 0 };
          return;
        }
        // Just a sample for suggestions/preview, not a required file — skip quietly rather than alarming the user with a size error for a non-authoritative pick.
        if (item.size && item.size > this.MAX_FILE_SIZE) return;
        const res = await this.DOWNLOAD_CLOUD_FILE({
          project_id: this.$route.query.pid,
          provider: item.provider,
          account_id: item.account_id,
          file_id: item.id,
        });
        if (this.cloudSuggestionItemId !== identity) return;
        const file = new File([res.data], item.name, {
          type: res.data.type || item.mime_type || "",
        });
        const sample = await processFile(file);
        if (this.cloudSuggestionItemId !== identity) return;
        if (!sample) throw new Error("Failed to create DataFrame from file");
        this.file = file;
        this.df = sample;
        this.cloudColumnSuggestions = sample.columns;
        this.filePreview = generateFilePreview(sample, this.PREVIEW_ROWS);
        const { columnStats, datetimeColumns } = generateColumnStats(sample, this.SAMPLE_SIZE);
        this.columnStats = columnStats;
        this.fileStats = { totalRows: sample.shape[0], totalColumns: sample.shape[1] };
        this.potentialDatetimeColumns = datetimeColumns;
        if (!this.isUpdateMode) {
          this.selectedDatetimeColumns = [...datetimeColumns];
          this.selectedPrimaryDatetimeColumn = datetimeColumns.length > 0 ? datetimeColumns[0] : null;
        }
        this.updatePrimaryOptions();
      } catch {
        if (this.cloudSuggestionItemId === identity) {
          this.cloudColumnSuggestions = [];
          this.potentialDatetimeColumns = [];
          this.df = null;
        }
      } finally {
        if (this.cloudSuggestionItemId === identity) this.isProcessing = false;
      }
    },
    // Cloud picks carry only metadata; for a lone file we fetch its bytes and
    // run the same parse pipeline a local upload would.
    async maybeLoadCloudPreview() {
      const item = this.previewableCloudItem;
      if (!item) {
        if (this.cloudPreviewItemId) {
          this.cloudPreviewItemId = null;
          this.file = null;
          this.resetState();
        }
        return;
      }
      if (this.cloudPreviewItemId === item.id) return;

      if (item.size && item.size > this.MAX_FILE_SIZE) {
        this.cloudPreviewItemId = item.id;
        this.resetState();
        this.cloudColumnSuggestions = [];
        this.fileError = `${this.$vuetify.lang.t('$vuetify.gpt_dataset.file_size_exceeded_error')} ${this.MAX_FILE_SIZE / 1024 / 1024}MB`;
        return;
      }

      this.cloudPreviewItemId = item.id;
      this.resetState();
      this.isProcessing = true;
      try {
        const res = await this.DOWNLOAD_CLOUD_FILE({
          project_id: this.$route.query.pid,
          provider: item.provider,
          account_id: item.account_id,
          file_id: item.id,
        });
        // A mid-flight selection change makes this response stale.
        if (this.cloudPreviewItemId !== item.id) return;
        const file = new File([res.data], item.name, {
          type: res.data.type || item.mime_type || "",
        });
        this.file = file;
        this.validateFile();
        this.df = await processFile(file);
        if (this.cloudPreviewItemId !== item.id) return;
        if (!this.df) {
          throw new Error('Failed to create DataFrame from file');
        }
        this.validateDataset();
        this.filePreview = generateFilePreview(this.df, this.PREVIEW_ROWS);
        const { columnStats, datetimeColumns } = generateColumnStats(this.df, this.SAMPLE_SIZE);
        this.columnStats = columnStats;
        this.potentialDatetimeColumns = datetimeColumns;
        this.cloudColumnSuggestions = this.df.columns;
        if (!this.isUpdateMode) {
          this.selectedDatetimeColumns = [...datetimeColumns];
          this.selectedPrimaryDatetimeColumn = datetimeColumns.length > 0 ? datetimeColumns[0] : null;
        }
        this.updatePrimaryOptions();
      } catch (err) {
        if (this.cloudPreviewItemId === item.id) {
          this.df = null;
          this.cloudColumnSuggestions = [];
          this.fileError = err.message;
        }
      } finally {
        if (this.cloudPreviewItemId === item.id) this.isProcessing = false;
      }
    },
    async handleFileUpload() {
      this.resetState();

      if (!this.file) return;

      try {
        this.validateFile();
        this.isProcessing = true;

        this.df = await processFile(this.file);

        if (!this.df) {
          throw new Error('Failed to create DataFrame from file');
        }

        if (this.df) {
          this.validateDataset();
          this.validateDatetimeFields();
          this.filePreview = generateFilePreview(this.df, this.PREVIEW_ROWS);
          const { columnStats, datetimeColumns } = generateColumnStats(this.df, this.SAMPLE_SIZE);

          this.columnStats = columnStats;
          this.potentialDatetimeColumns = datetimeColumns;

          if (!this.isUpdateMode) {
            this.selectedDatetimeColumns = [...datetimeColumns];
            this.selectedPrimaryDatetimeColumn = datetimeColumns.length > 0 ? datetimeColumns[0] : null;
          }

          this.updatePrimaryOptions();

          this.UPDATE_GPT_DATASET_STATE({
            dataset: { ...this.dataset, datasetFile: this.file },
          });
        } else {
          throw new Error(this.$vuetify.lang.t('$vuetify.gpt_dataset.file_preview_creation_error'));
        }
      } catch (err) {
        this.fileError = err.message;
      } finally {
        this.isProcessing = false;
      }
    },
  },
};
</script>

<style scoped>
.no-dropdown-icon >>> .v-input__append-inner .v-icon {
  display: none !important;
}
.info-icon {
  display: inline-block;
  width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  border-radius: 50%;
  background-color: #e0e0e0;
  color: #333;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  margin-left: 8px;
}
.append-icons {
  display: flex;
  align-items: center;
  gap: 4px;
}

.dataset-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.tag-badges {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
}

/* Make chips clickable */
.v-chip.clickable {
  cursor: pointer;
  transition: all 0.2s;
}

.v-chip.clickable:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

/* Column alignment styles */
.header-row {
  border-bottom: 1px solid #e0e0e0;
}

.data-row {
  border-bottom: 1px solid #f5f5f5;
}

.dataset-name-column {
  min-width: 200px;
  max-width: 200px;
  flex-shrink: 0;
  padding-right: 16px;
}

.tags-column {
  flex: 1;
  min-width: 0;
}

.dataset-name-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tooltip-text {
  font-size: 0.875rem;
}

.dataset-file-input >>> .v-input__prepend-outer {
  margin-top: 14px !important;
}

.preview-table-container {
  max-height: 60vh;
  overflow-y: auto;
}

.stats-table-container {
  max-height: 50vh;
  overflow-y: auto;
}

.sample-values {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.column-name {
  word-break: break-all;
}

</style>
